/**
 * Currency Shop session state — real-money Gold. Mirrors `vendor-state.ts`'s
 * `createStore` + scoped `economy:updated` balance-sync pattern.
 *
 * The purchase is core's deposit flow (`frontend/src/core/payments`): Proceed
 * creates an order (`POST /api/payments/deposits`, priced on the server by the
 * game's `GoldCatalogue`) and the browser goes to the gateway's page. It comes
 * back to `/play?order=<id>`; `resumeReturnedDeposit` polls that order and the
 * Gold lands over `economy:updated` once core's ledger has credited it.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import { socket } from '../../core/network/socket';
import { getPaymentMethods, toPaymentError } from '../../core/payments/payments-api';
import { clearPendingOrder, pollDepositStatus, startDeposit } from '../../core/payments/deposit-flow';
import type { DepositMethodId, DepositOrder } from '../../core/payments/types';
import type {
  CurrencyShopState, CurrencyShopBalance, CurrencyShopView, CurrencyPackEntry, PaymentMethod,
} from './currency-shop-state.types';

/** Where the gateway sends the player back; core appends `?order=<orderId>`. */
export const DEPOSIT_RETURN_PATH = '/play';

/**
 * Which radio to open on.
 *
 * Janeks: *"Currency depends on what payment method is selected, default =
 * currency from profile. If default currency does not correspond to the
 * currency of the payment method, then upon opening the payment view, select
 * the payment method that corresponds to default currency (1st option)."*
 *
 * So: the first ENABLED method that can charge in the player's own currency,
 * and the first enabled method otherwise. Disabled methods are never selected
 * even when they are the only currency match — BLIK is the live example, and
 * opening on a method that cannot be used would be a dead form.
 *
 * With one method enabled this always resolves to Card. It is written as the
 * rule rather than as that answer because the rule is what survives the second
 * method being switched on.
 */
export function defaultPaymentMethod(
  methods: PaymentMethod[],
  preferredCurrency: string | null,
): string | null {
  const usable = methods.filter((m) => m.enabled);
  if (preferredCurrency) {
    const match = usable.find((m) => m.currencies.includes(preferredCurrency));
    if (match) return match.id;
  }
  return usable[0]?.id ?? null;
}

const INITIAL_STATE: CurrencyShopState = {
  packs: [],
  balance: null,
  customMinCents: null,
  customMaxCents: null,
  customCents: null,
  preferredCurrency: null,
  methods: [],
  paymentMethodId: null,
  busy: false,
  error: null,
  needsBilling: false,
  lastReceipt: null,
};

export const currencyShopStateStore = createStore<CurrencyShopState>({ ...INITIAL_STATE });

let unsubscribeBalance: (() => void) | null = null;

function connectBalanceSync(): void {
  disconnectBalanceSync();
  const onBalanceUpdated = (balance: CurrencyShopBalance) => {
    currencyShopStateStore.set((s) => ({ ...s, balance }));
  };
  socket.on('economy:updated', onBalanceUpdated);
  unsubscribeBalance = () => socket.off('economy:updated', onBalanceUpdated);
}

function disconnectBalanceSync(): void {
  unsubscribeBalance?.();
  unsubscribeBalance = null;
}

/**
 * Core's deposit methods as the chips need them. Unreachable payments (the
 * endpoint failing) read as "no method available", never as an error that
 * hides the ladder.
 */
async function loadMethods(): Promise<PaymentMethod[]> {
  try {
    const res = await getPaymentMethods();
    return res.allDeposit.map((m) => ({
      id: m.id,
      label: m.label,
      enabled: m.enabled,
      currencies: m.currencies,
      billingRequired: m.billingRequired,
    }));
  } catch {
    return [];
  }
}

/** Opens the shop: fetches the ladder + bounds + currency + methods, and starts the balance push. */
export async function loadCurrencyShop(): Promise<void> {
  connectBalanceSync();
  currencyShopStateStore.set((s) => ({ ...s, busy: true, error: null, needsBilling: false }));
  try {
    const [view, methods] = await Promise.all([
      api.get<CurrencyShopView>('/currency-shop'),
      loadMethods(),
    ]);
    currencyShopStateStore.set((s) => ({
      ...s,
      // `?? []` is not defensive decoration: `packs` is a non-optional array on
      // `CurrencyShopState`, and writing `undefined` into it made
      // `render`'s `state.packs.length` throw from inside a store push — an
      // UNHANDLED REJECTION, which vitest attributes to whichever test happened
      // to be running. That is the red in the suite; the type said it could not
      // happen because the response was trusted to match its interface.
      packs: view.packs ?? [],
      customMinCents: view.customMinCents,
      customMaxCents: view.customMaxCents,
      // The slider opens at its floor rather than at a guessed midpoint: the
      // floor is the only value on it that is definitely for sale.
      customCents: s.customCents ?? view.customMinCents,
      preferredCurrency: view.preferredCurrency,
      methods,
      paymentMethodId: s.paymentMethodId && methods.some((m) => m.id === s.paymentMethodId && m.enabled)
        ? s.paymentMethodId
        : defaultPaymentMethod(methods, view.preferredCurrency),
      busy: false,
    }));
  } catch (err) {
    currencyShopStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t load the Gold packs.') }));
  }
}

/**
 * The pack the current amount IS, or null for an in-between amount.
 *
 * ─── Derived, deliberately not stored ───────────────────────────────────────
 * Janeks: *"Select pack - it is highlighted and the slider amount is bumped to
 * it (slider is like a checkout step, only always there). Then press proceed"*.
 *
 * The obvious implementation is a `selectedPackKey` field, and it desyncs the
 * first time the player drags the slider: the highlight would stay on a pack
 * whose price is no longer the amount being charged. Deriving it from
 * `customCents` makes that impossible — dragging ONTO a pack's price lights it
 * up, dragging a step off puts it out, and there is no second source of truth
 * to keep in step.
 */
export function selectedPack(state: CurrencyShopState): CurrencyPackEntry | null {
  if (state.customCents === null) return null;
  return state.packs.find((p) => p.priceCents === state.customCents) ?? null;
}

/** Clicking a pack card: bumps the checkout amount to its price. That IS the selection. */
export function selectPack(packKey: string): void {
  const pack = currencyShopStateStore.get().packs.find((p) => p.key === packKey);
  if (pack) setCustomCents(pack.priceCents);
}

export function setCustomCents(cents: number): void {
  currencyShopStateStore.set((s) => {
    if (s.customMinCents === null || s.customMaxCents === null) return s;
    return { ...s, customCents: clamp(cents, s.customMinCents, s.customMaxCents) };
  });
}

export function setPaymentMethod(id: string): void {
  currencyShopStateStore.set((s) => {
    const method = s.methods.find((m) => m.id === id);
    return method?.enabled ? { ...s, paymentMethodId: id, error: null, needsBilling: false } : s;
  });
}

/**
 * The currency the gateway charges in: the player's own when the method takes
 * it, else EUR when it does, else whatever the method does take. Pricing is in
 * EUR either way — core converts.
 */
export function chargeCurrency(method: PaymentMethod, preferred: string | null): string {
  if (preferred && method.currencies.includes(preferred)) return preferred;
  if (method.currencies.includes('EUR') || method.currencies.length === 0) return 'EUR';
  return method.currencies[0]!;
}

/**
 * The one checkout action — Proceed.
 *
 * Exactly a pack's price buys THE PACK (`productId`), anything else is a
 * custom amount (`amountCents`, EUR cents). Both are priced by the server's
 * `GoldCatalogue`, which also fixes the Gold the order grants; the slider's
 * own preview is never sent. The pack id is not about the number — at a pack's
 * price both pay the same — it is attribution: the order, the invoice line and
 * any refund then name the pack.
 *
 * On success the browser leaves for the gateway, so `busy` stays true: the
 * panel reads as "handing off to payment", not as done and idle.
 */
export async function proceed(): Promise<void> {
  const state = currencyShopStateStore.get();
  const method = state.methods.find((m) => m.id === state.paymentMethodId && m.enabled);
  if (!method || state.customCents === null) return;
  const pack = selectedPack(state);
  currencyShopStateStore.set((s) => ({ ...s, busy: true, error: null, needsBilling: false, lastReceipt: null }));
  try {
    await startDeposit({
      method: method.id as DepositMethodId,
      ...(pack ? { productId: pack.key } : { amountCents: state.customCents }),
      currency: chargeCurrency(method, state.preferredCurrency),
      returnPath: DEPOSIT_RETURN_PATH,
    });
  } catch (err) {
    const e = toPaymentError(err);
    currencyShopStateStore.set((s) => ({
      ...s, busy: false, error: depositErrorText(e.code, e.message), needsBilling: e.code === 'BILLING_ADDRESS_REQUIRED',
    }));
  }
}

/** Core's deposit refusals, in words a player can act on. */
export function depositErrorText(code: string, fallback: string): string {
  switch (code) {
    case 'BILLING_ADDRESS_REQUIRED': return 'This payment method needs your full billing address. Add it in Settings, then try again.';
    case 'EMAIL_NOT_VERIFIED': return 'Verify your e-mail address first — the link is in the e-mail we sent when you signed up.';
    case 'KYC_REQUIRED': return 'Payments this large need a verified identity first.';
    case 'PAYMENTS_NOT_ADOPTED':
    case 'PAYMENT_METHOD_UNAVAILABLE': return 'That payment method isn\'t available right now.';
    case 'CURRENCY_NOT_SUPPORTED': return 'That payment method can\'t charge in this currency.';
    case 'TOO_MANY_PENDING': return 'You have unfinished payments. Complete them or wait a few minutes.';
    case 'AMOUNT_OUT_OF_RANGE': return 'That amount can\'t be bought.';
    default: return fallback || 'Couldn\'t start the payment.';
  }
}

/**
 * Back from the gateway: `/play?order=<orderId>`. Polls the order until core
 * reports a final state, then says how it went — the Gold itself arrives over
 * `economy:updated` (core credits on the webhook, which can land before or
 * after the browser does). Returns false when the URL carries no order.
 *
 * Only `?order=` counts, not core's sessionStorage copy: a stored id would
 * re-announce an old payment on every later page load.
 */
export async function resumeReturnedDeposit(
  location: Pick<Location, 'search' | 'pathname' | 'hash'> = window.location,
  opts: { intervalMs?: number; maxTries?: number } = {},
): Promise<boolean> {
  const orderId = new URLSearchParams(location.search).get('order');
  if (!orderId) return false;
  try {
    const params = new URLSearchParams(location.search);
    params.delete('order');
    const rest = params.toString();
    history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
  } catch { /* a missing history API must not lose the result */ }

  currencyShopStateStore.set((s) => ({ ...s, busy: true, error: null, needsBilling: false, lastReceipt: null }));
  try {
    const order = await pollDepositStatus(orderId, opts);
    clearPendingOrder();
    currencyShopStateStore.set((s) => ({ ...s, busy: false, ...depositOutcome(order) }));
  } catch (err) {
    currencyShopStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t check that payment.') }));
  }
  return true;
}

/** One order's final (or last seen) state as the panel's receipt / error line. */
export function depositOutcome(order: DepositOrder): Pick<CurrencyShopState, 'lastReceipt' | 'error'> {
  switch (order.status) {
    case 'APPROVED':
      return { lastReceipt: 'Payment received — your Gold is in.', error: null };
    case 'DECLINED':
    case 'ERROR':
      return { lastReceipt: null, error: 'The payment was declined. Nothing was charged.' };
    case 'EXPIRED':
      return { lastReceipt: null, error: 'The payment was not completed in time. Nothing was charged.' };
    case 'MISMATCH':
      return { lastReceipt: null, error: 'The payment needs a manual check. Support will sort it out — no need to pay again.' };
    default:
      return { lastReceipt: 'Payment is still processing — your Gold arrives as soon as it clears.', error: null };
  }
}

export function closeCurrencyShop(): void {
  disconnectBalanceSync();
  currencyShopStateStore.set({ ...INITIAL_STATE });
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(n)));
}

function msg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
